import React from 'react'
import { css, cx } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { Dropdown } from '../dropdown'
import { DropdownStyleOverrides } from '../dropdown/dropdown'
import { MenuItemRenderer } from '../dropdown/menu'
import { QuickFilter } from '.'
import { H2, H3 } from '../heading'

const styleOverrides: DropdownStyleOverrides = {
  outerContainerCss: css`
    width: auto;
    display: flex;
  `,
  selectionContainerCss: css`
    background-image: none;
    border: none;
    padding: 0;
    max-width: 400px;

    svg {
      margin-top: 2px;
    }
  `,
  menuCss: css`
    width: auto;
    min-width: 137px;
    max-width: 400px;
    max-height: initial;
    border-width: 1px;
    top: -5px;
    padding: 8px 0;
    box-shadow: 0px 2px 10px -4px ${theme.color.neutral50};
  `,
  menuItemContainerCss: css`
    padding: 4px 10px;
  `
}

const itemFlexCss = css`
  display: flex;
`

const nameCss = css`
  flex: 0 1 auto;
  white-space: nowrap;
  overflow: visible;
  text-overflow: ellipsis;
`

const countCss = css`
  color: ${theme.color.neutral60};
  margin-left: 4px;
  font-weight: normal;
  font-family: ${deprecatedTheme.fontFamilies.secondary};
  flex: 1 0 auto;
`

const disabledItemCss = css`
  opacity: 0.4;
`

const selectionNameCss = cx(
  nameCss,
  css`
    font-family: ${deprecatedTheme.fontFamilies.primaryCondensed};
    color: ${theme.color.turquoise70};
  `
)

const selectionCountCss = cx(
  countCss,
  css`
    margin-top: -1px;
    font-weight: 300;
  `
)

const selectionContainerCss = cx(
  itemFlexCss,
  css`
    margin: 0;
    line-height: 1.2em;
  `
)

const menuItemRenderer: MenuItemRenderer<QuickFilter> = ({
  item: { name, count },
  isDisabled
}) => (
  <div
    data-e2e={`quick-filter-option-${name}`}
    data-testid={`quick-filter-option-${name}`}
    className={cx(itemFlexCss, { [disabledItemCss]: isDisabled })}
  >
    <div className={nameCss}>{name}</div>
    <div className={countCss} data-testid={`quick-filter-option-${name}-value`}>
      ({typeof count === 'undefined' ? '?' : count})
    </div>
  </div>
)

export interface QuickFilterDropdownProps {
  readonly filters: QuickFilter[]
  readonly selectedFilter?: string
  readonly onChange: (item: string) => void
  readonly filterToGroup?: (filter: QuickFilter) => string
  readonly showQuickFilter?: (filter: QuickFilter) => boolean
  readonly isDrilldown?: boolean
}

interface State {
  readonly selectedFilter: QuickFilter
}

export class QuickFilterDropdown extends React.Component<
  QuickFilterDropdownProps,
  State
> {
  private readonly defaultQuickFilter: QuickFilter = {
    id: 'ALL',
    name: 'All'
  }

  constructor(props) {
    super(props)
    const { filters, selectedFilter } = props

    this.state = {
      selectedFilter:
        this.findFilterByID(filters, selectedFilter) || this.defaultQuickFilter
    }
  }

  public componentWillReceiveProps({
    filters: newFilters,
    selectedFilter: newSelectedFilter
  }: QuickFilterDropdownProps) {
    const { id: currentID, count: currentCount } = this.state.selectedFilter

    if (
      newSelectedFilter &&
      this.isValidFilter(newFilters, newSelectedFilter)
    ) {
      const newFilter: QuickFilter | undefined = this.findFilterByID(
        newFilters,
        newSelectedFilter
      )
      if (
        newFilter &&
        (currentID !== newSelectedFilter || newFilter.count !== currentCount)
      ) {
        this.setState({
          selectedFilter: newFilter
        })
      }
    } else {
      // Filter doesn't exist, set to default
      this.props.onChange(this.defaultQuickFilter.id)
    }
  }

  public render() {
    const { selectedFilter } = this.state
    const {
      filters,
      onChange,
      filterToGroup,
      showQuickFilter,
      isDrilldown = false
    } = this.props

    return (
      <Dropdown<QuickFilter>
        items={!!showQuickFilter ? filters.filter(showQuickFilter) : filters}
        selectedItem={selectedFilter}
        itemToString={({ name }) => name}
        itemToKey={({ id }) => id}
        itemToGroup={filterToGroup}
        isItemDisabled={({ count }) =>
          typeof count === 'undefined' || count === 0
        }
        onChange={({ id }) => onChange(id)}
        styleOverrides={styleOverrides}
        selectionItemRenderer={({ item: { name, count } }) => {
          const Tag = isDrilldown ? H3 : H2
          return (
            <Tag className={selectionContainerCss}>
              <div
                data-testid={`quick-filter-option-${name}`}
                data-e2e="current-quick-filter-selection-name"
                className={selectionNameCss}
              >
                {name}
              </div>
              <div
                data-testid={`current-quick-filter-selection-count`}
                data-e2e="current-quick-filter-selection-count"
                className={selectionCountCss}
              >
                ({typeof count === 'undefined' ? '?' : count})
              </div>
            </Tag>
          )
        }}
        menuItemRenderer={menuItemRenderer}
        aria-label="Quick Filter"
      />
    )
  }

  private findFilterByID = (filters: QuickFilter[], filterID: string) => {
    return filters.find(({ id }) => filterID === id)
  }

  private isValidFilter = (filters: QuickFilter[], filterID: string) => {
    const filter = this.findFilterByID(filters, filterID)
    if (!filter && filters.length > 0) {
      return false
    }
    return true
  }
}
